import { useCallback, useEffect, useState } from 'react';

import { localStorageAvailable } from '@utils/storage-available';

import { useGetMyProfileQuery } from '@redux/api/user';

interface VersionedData<T> {
  version: string;
  data: T;
}

interface UseLocalStorageOptions {
  version?: string;
  userId?: boolean;
}

export default function useLocalStorage<ValueType>(
  key: string,
  defaultValue: ValueType,
  options?: UseLocalStorageOptions,
) {
  const storageAvailable = localStorageAvailable();
  const includeUserId = options?.userId ?? false;

  const { data: currentUser } = useGetMyProfileQuery(undefined, {
    skip: !includeUserId,
  });

  const userId = currentUser?.id;
  const version = options?.version;

  const getStorageKey = useCallback(
    (forStorage: boolean) => {
      if (version !== undefined && userId && includeUserId && forStorage) {
        return `${key}_${userId}`;
      }
      return key;
    },
    [key, userId, version, includeUserId],
  );

  const [value, setValue] = useState(() => {
    if (!storageAvailable) return defaultValue;

    const newKey = getStorageKey(true);
    let storedValue = localStorage.getItem(newKey);
    let migratedFromOldKey = false;

    if (storedValue === null && version !== undefined && userId && includeUserId) {
      const oldKey = getStorageKey(false);
      storedValue = localStorage.getItem(oldKey);

      if (storedValue !== null) {
        migratedFromOldKey = true;
      }
    }

    if (storedValue === null) return defaultValue;

    try {
      const parsed = JSON.parse(storedValue);

      if (version !== undefined) {
        if (parsed && typeof parsed === 'object' && 'version' in parsed && 'data' in parsed) {
          const versionedData = parsed as VersionedData<ValueType>;

          if (versionedData.version !== version) {
            return defaultValue;
          }

          if (migratedFromOldKey && userId) {
            localStorage.removeItem(getStorageKey(false));
          }

          return versionedData.data;
        } else {
          return defaultValue;
        }
      }

      if (migratedFromOldKey && userId) {
        localStorage.removeItem(getStorageKey(false));
      }

      return parsed;
    } catch {
      return defaultValue;
    }
  });

  useEffect(() => {
    const storageKey = getStorageKey(true);

    const listener = (e: StorageEvent) => {
      if (e.storageArea === localStorage && e.key === storageKey) {
        setValue(e.newValue ? JSON.parse(e.newValue) : e.newValue);
      }
    };
    window.addEventListener('storage', listener);

    return () => {
      window.removeEventListener('storage', listener);
    };
  }, [getStorageKey]);

  const setValueInLocalStorage = useCallback(
    (newValue: ValueType) => {
      setValue((currentValue: ValueType) => {
        const result = typeof newValue === 'function' ? newValue(currentValue) : newValue;

        if (storageAvailable) {
          const dataToStore = version !== undefined ? { version, data: result } : result;
          const storageKey = getStorageKey(true);

          localStorage.setItem(storageKey, JSON.stringify(dataToStore));
        }

        return result;
      });
    },
    [getStorageKey, storageAvailable, version],
  );

  return [value, setValueInLocalStorage];
}
